import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import useDocumentTitle from "./hooks/useDocumentTitle";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clientes from "./pages/Clientes";
import ClienteDetalle from "./pages/ClienteDetalle";
import Servicios from "./pages/Servicios";
import ServicioDetalle from "./pages/ServicioDetalle";
import ServicioTimeline from "./pages/ServicioTimeline";
import LineaTiempoIndex from "./pages/LineaTiempoIndex";
import TimelineGlobal from "./pages/TimelineGlobal";
import Pagos from "./pages/Pagos";
import Notificaciones from "./pages/Notificaciones";
import Usuarios from "./pages/Usuarios";
import Configuracion from "./pages/Configuracion";
import Empresas from "./pages/Empresas";
import RutasCobro from "./pages/RutasCobro";
import EdpAprobar from "./pages/EdpAprobar";
import Propuesta from "./pages/Propuesta";
import Aprende from "./pages/Aprende";
import Layout from "./components/Layout";
import "./App.css";

// Protected Route component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    if (requiredRoles.length > 0 && !requiredRoles.includes(user.rol)) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

// Public Route component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

function AppRoutes() {
    // Use hook to set document title dynamically from platform config
    useDocumentTitle();
    
    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route path="/edp/aprobar/:token" element={<EdpAprobar />} />
            <Route path="/propuesta" element={<Propuesta />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Dashboard />} />
                <Route path="aprende" element={<Aprende />} />
                <Route path="clientes" element={<Clientes />} />
                <Route path="clientes/:id" element={<ClienteDetalle />} />
                <Route path="servicios" element={<Servicios />} />
                <Route path="servicios/mes" element={<Servicios soloDelMes={true} />} />
                <Route path="linea-de-tiempo" element={<LineaTiempoIndex />} />
                <Route path="servicios/timeline-global" element={<TimelineGlobal />} />
                <Route path="servicios/:id" element={<ServicioDetalle />} />
                <Route path="servicios/:id/timeline" element={<ServicioTimeline />} />
                <Route path="pagos" element={<Pagos />} />
                <Route path="notificaciones" element={<Notificaciones />} />
                <Route path="rutas-cobro" element={<RutasCobro />} />
                <Route path="usuarios" element={<Usuarios />} />
                <Route path="configuracion" element={<Configuracion />} />
                <Route 
                    path="empresas" 
                    element={
                        <ProtectedRoute requiredRoles={['superadmin']}>
                            <Empresas />
                        </ProtectedRoute>
                    } 
                />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
